import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaBroadcastTower, FaCalendarAlt, FaFileAlt, FaMapMarkerAlt } from "react-icons/fa";
import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import API from "../../api/api";
import { resolveAssetUrl } from "../../api/config";
import PageSectionsRenderer from "../../components/common/PageSectionsRenderer";

const formatDate = (raw) => {
  if (!raw) return "Date TBD";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
};

const formatEventDateRange = (startRaw, endRaw) => {
  const start = startRaw || null;
  const end = endRaw || startRaw || null;
  if (!start && !end) return "Date TBD";
  if (!start) return formatDate(end);
  if (!end || end === start) return formatDate(start);
  return `${formatDate(start)} to ${formatDate(end)}`;
};

const keyOf = (value) => String(value || "").trim().toLowerCase();

const UpdatesResults = () => {
  const [sections, setSections] = useState([]);
  const [liveFeed, setLiveFeed] = useState({ enabled: true, pause_message: "", items: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const [sectionRes, liveRes] = await Promise.all([
          API.fetchAboutSections("updates_results"),
          API.fetchCurrentLiveResults(),
        ]);
        if (!mounted) return;
        setSections(Array.isArray(sectionRes) ? sectionRes : []);
        setLiveFeed({
          enabled: liveRes?.enabled !== false,
          pause_message: liveRes?.pause_message || "",
          items: Array.isArray(liveRes?.items) ? liveRes.items : [],
        });
      } catch (err) {
        if (!mounted) return;
        setError(err?.message || "Failed to load updates and results.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(async () => {
      try {
        const liveRes = await API.fetchCurrentLiveResults();
        setLiveFeed({
          enabled: liveRes?.enabled !== false,
          pause_message: liveRes?.pause_message || "",
          items: Array.isArray(liveRes?.items) ? liveRes.items : [],
        });
      } catch (_err) {
        // Silent refresh failure; keep previous live feed.
      }
    }, 20000);

    return () => clearInterval(timer);
  }, []);

  const sectionMap = useMemo(() => {
    const map = {};
    (Array.isArray(sections) ? sections : [])
      .filter((s) => s?.is_active !== false)
      .forEach((s) => {
        map[keyOf(s.section_key)] = s;
      });
    return map;
  }, [sections]);

  const liveMeta = sectionMap["updates_live_meta"] || null;
  const pageMeta = sectionMap["updates_results_meta"] || null;

  const liveEvents = useMemo(() => {
    const grouped = new Map();
    (Array.isArray(liveFeed?.items) ? liveFeed.items : []).forEach((row) => {
      const id = String(row?.event_id || "");
      if (!id) return;
      if (!grouped.has(id)) {
        grouped.set(id, {
          event_id: row.event_id,
          event_title: row.event_title || "Live Event",
          event_date: row.event_date || null,
          event_end_date: row.event_end_date || null,
          location: row.location || "Location TBD",
          latest_updated_at: row.updated_at || null,
          rows: [],
        });
      }
      const bucket = grouped.get(id);
      bucket.rows.push(row);
      if (row?.updated_at && (!bucket.latest_updated_at || new Date(row.updated_at) > new Date(bucket.latest_updated_at))) {
        bucket.latest_updated_at = row.updated_at;
      }
    });

    return Array.from(grouped.values())
      .map((group) => ({
        ...group,
        rows: group.rows
          .slice()
          .sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))
          .slice(0, 8),
      }))
      .sort((a, b) => new Date(b.latest_updated_at || 0) - new Date(a.latest_updated_at || 0));
  }, [liveFeed]);

  const resultSheets = useMemo(() => {
    return (Array.isArray(sections) ? sections : [])
      .filter((s) => s?.is_active !== false)
      .filter((s) => keyOf(s.section_key).startsWith("updates_result_sheet_"))
      .sort((a, b) => Number(a?.sort_order || 0) - Number(b?.sort_order || 0));
  }, [sections]);

  return (
    <div className="bg-slate-50 min-h-screen">
      <Navbar />

      <section className="bg-white border-b border-slate-200 py-14 px-6 text-center">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900">
            {pageMeta?.heading || "Updates & Results"}
          </h1>
          <p className="text-lg text-slate-600 mt-3">
            {pageMeta?.subheading || "Track live updates and access latest published result sheets."}
          </p>
        </div>
      </section>

      <main className="max-w-screen-xl mx-auto px-6 py-14 space-y-14">
        {loading ? <div className="text-center text-slate-500">Loading updates...</div> : null}
        {error ? <div className="text-center text-red-600">{error}</div> : null}

        {!loading && !error ? (
          <>
            <section className="bg-white rounded-2xl shadow p-6 md:p-8">
              <div className="flex items-center gap-3 mb-2">
                <FaBroadcastTower className="text-blue-600" />
                <h2 className="text-2xl font-bold text-slate-900">
                  {liveMeta?.heading || "Live Updates"}
                </h2>
              </div>
              <p className="text-slate-600 mb-6">
                {liveMeta?.subheading ||
                  "Live match updates are available for currently open events."}
              </p>

              {!liveFeed?.enabled ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-center text-amber-800">
                  {liveFeed?.pause_message || "Live results are paused by admin."}
                </div>
              ) : liveEvents.length === 0 ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-center text-slate-500">
                  No live results available right now. Please check back soon.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {liveEvents.map((event) => (
                    <article key={event.event_id} className="rounded-xl border border-slate-200 p-5 bg-slate-50">
                      <h3 className="font-semibold text-lg text-slate-900">{event.event_title}</h3>
                      <div className="mt-3 text-sm text-slate-600 space-y-1">
                        <p className="flex items-center gap-2">
                          <FaCalendarAlt className="text-slate-500" />
                          {formatEventDateRange(event.event_date, event.event_end_date)}
                        </p>
                        <p className="flex items-center gap-2">
                          <FaMapMarkerAlt className="text-slate-500" />
                          {event.location || "Location TBD"}
                        </p>
                      </div>
                      <div className="mt-4 rounded-lg border border-slate-200 bg-white p-3">
                        <p className="text-xs text-slate-500 mb-2">Latest Results</p>
                        <ul className="space-y-2">
                          {event.rows.map((row, idx) => (
                            <li key={`${event.event_id}-${idx}`} className="text-sm text-slate-700">
                              <span className="font-semibold">{row.player_name || "Player"}</span>
                              {" - "}
                              <span>{row.game_name || "Game"}</span>
                              {row.result_position ? (
                                <span className="ml-2 text-blue-700 font-semibold">#{row.result_position}</span>
                              ) : null}
                              {row.result_time ? (
                                <span className="ml-2 text-slate-500">Time: {row.result_time}</span>
                              ) : null}
                              {row.points_score !== null && row.points_score !== undefined ? (
                                <span className="ml-2 text-slate-500">Pts: {row.points_score}</span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="mt-4">
                        <Link
                          to={`/live-score/${event.event_id}`}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
                        >
                          <FaBroadcastTower />
                          Open Live Updates
                        </Link>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>

            <section className="bg-white rounded-2xl shadow p-6 md:p-8">
              <div className="flex items-center gap-3 mb-2">
                <FaFileAlt className="text-indigo-600" />
                <h2 className="text-2xl font-bold text-slate-900">Result Sheets</h2>
              </div>
              <p className="text-slate-600 mb-6">
                Published result sheets and updates uploaded by the admin team.
              </p>

              {resultSheets.length === 0 ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-center text-slate-500">
                  No result sheets published yet.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {resultSheets.map((sheet) => (
                    <article key={sheet.id} className="rounded-xl border border-slate-200 overflow-hidden bg-slate-50">
                      <div className="h-44 bg-slate-200">
                        <img
                          src={resolveAssetUrl(sheet.image_path || "", {
                            folder: "page-sections",
                            defaultPath: "https://placehold.co/900x500?text=Result+Sheet",
                          })}
                          alt={sheet.heading || "Result sheet"}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = "https://placehold.co/900x500?text=Result+Sheet";
                          }}
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-slate-900">{sheet.heading || "Result Sheet"}</h3>
                        {sheet.subheading ? (
                          <p className="text-xs uppercase tracking-wide text-blue-700 mt-1">{sheet.subheading}</p>
                        ) : null}
                        {sheet.body ? (
                          <p className="text-sm text-slate-600 mt-2 whitespace-pre-line">{sheet.body}</p>
                        ) : null}
                        <div className="mt-4">
                          {sheet.cta_link ? (
                            <a
                              href={sheet.cta_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition-colors"
                            >
                              <FaFileAlt />
                              {sheet.cta_label || "Open Result Sheet"}
                            </a>
                          ) : (
                            <span className="text-sm text-slate-400">Result link not added.</span>
                          )}
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>

            <PageSectionsRenderer
              pageKey="updates_results"
              className="pt-0"
              excludeKeys={["updates_results_meta", "updates_live_meta"]}
              excludeKeyPrefixes={["updates_result_sheet_"]}
            />
          </>
        ) : null}
      </main>

      <Footer />
    </div>
  );
};

export default UpdatesResults;
